// Shared Parent Portal UI primitives (Aug 2026 redesign).
//
// Six reusable pieces the redesigned pages compose from, so per-page
// card/KPI/empty-state styling doesn't drift into 500-line duplication:
//
//   PortalCard           — the shared surface (border + soft shadow +
//                          optional hover lift + optional accent stripe)
//   PortalKpi            — big-number + label KPI tile with tinted icon
//   PortalSectionHeader  — eyebrow / title / optional action button pair
//   PortalEmptyState     — icon + title + body + optional CTA
//   PortalStatusChip     — sm/md status pill with semantic tint
//   ChildIdentityCard    — the "Viewing Student" premium selector card
//                          (rendered by ChildSelectorBar; the picker
//                          menu still lives in index.tsx)

import React from "react";
import { Box, Typography, Avatar, alpha } from "@mui/material";
import { pp, PP_C } from "./theme";

type PortalCardProps = {
  children: React.ReactNode;
  accent?: "green" | "amber" | "red" | "blue" | "navy" | null;
  hover?: boolean;
  padding?: number | string;
  sx?: any;
  onClick?: () => void;
};

/**
 * Shared card surface. Every page card should route through this so
 * border/shadow/radius decisions are made in one place.
 */
export const PortalCard: React.FC<PortalCardProps> = ({
  children, accent = null, hover = false, padding = 2.5, sx, onClick,
}) => {
  const accentColor = accent === "green" ? pp.color.green
    : accent === "amber" ? pp.color.amber
    : accent === "red"   ? pp.color.red
    : accent === "blue"  ? pp.color.blue
    : accent === "navy"  ? pp.color.navy
    : null;

  return (
    <Box
      onClick={onClick}
      sx={{
        position: "relative",
        bgcolor: pp.color.card,
        border: `1px solid ${pp.color.border}`,
        borderRadius: `${pp.radius.md}px`,
        boxShadow: pp.shadow.card,
        p: padding,
        transition: pp.transition,
        cursor: onClick ? "pointer" : "default",
        ...(hover && {
          "&:hover": {
            transform: "translateY(-2px)",
            boxShadow: pp.shadow.cardHover,
            borderColor: pp.color.borderStrong,
          },
        }),
        ...(accentColor && {
          "&::before": {
            content: '""',
            position: "absolute", left: 0, top: 12, bottom: 12, width: 3,
            borderRadius: 3, bgcolor: accentColor,
          },
        }),
        ...sx,
      }}
    >
      {children}
    </Box>
  );
};

type PortalKpiProps = {
  label: string;
  value: React.ReactNode;
  icon: React.ReactNode;
  accent?: "green" | "amber" | "red" | "blue" | "navy";
  hint?: React.ReactNode;
  onClick?: () => void;
  active?: boolean;
};

/**
 * Metric tile. Uses a tinted icon container matching the accent so
 * KPI rows read at a glance without becoming a bright color soup.
 */
export const PortalKpi: React.FC<PortalKpiProps> = ({
  label, value, icon, accent = "green", hint, onClick, active,
}) => {
  const color = accent === "green" ? pp.color.green
    : accent === "amber" ? pp.color.amber
    : accent === "red"   ? pp.color.red
    : accent === "blue"  ? pp.color.blue
    : pp.color.navy;
  const surface = accent === "green" ? pp.surface.mint
    : accent === "amber" ? pp.surface.amber
    : accent === "red"   ? pp.surface.red
    : accent === "blue"  ? pp.surface.blue
    : pp.surface.navy;

  return (
    <PortalCard hover={!!onClick} padding={2} onClick={onClick}
      sx={{
        minWidth: 0,
        ...(active && {
          borderColor: color,
          boxShadow: `0 0 0 1px ${alpha(color, 0.25)}, ${pp.shadow.cardHover}`,
        }),
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, mb: 1 }}>
        <Box sx={{
          width: 34, height: 34, borderRadius: `${pp.radius.sm}px`,
          bgcolor: surface, color, display: "flex",
          alignItems: "center", justifyContent: "center",
          "& svg": { fontSize: 18 },
        }}>
          {icon}
        </Box>
        <Typography sx={{
          fontSize: 11, fontWeight: 700, color: pp.color.muted,
          textTransform: "uppercase", letterSpacing: "0.06em",
        }}>
          {label}
        </Typography>
      </Box>
      <Typography sx={{
        fontSize: 28, fontWeight: 800, color: pp.color.text,
        lineHeight: 1.1, letterSpacing: "-0.02em",
      }}>
        {value}
      </Typography>
      {hint && (
        <Typography sx={{ fontSize: 11.5, color: pp.color.muted, mt: 0.5 }}>
          {hint}
        </Typography>
      )}
    </PortalCard>
  );
};

type PortalSectionHeaderProps = {
  title: string;
  eyebrow?: string;
  action?: React.ReactNode;
  icon?: React.ReactNode;
};

export const PortalSectionHeader: React.FC<PortalSectionHeaderProps> = ({
  title, eyebrow, action, icon,
}) => (
  <Box sx={{
    display: "flex", alignItems: "center", justifyContent: "space-between",
    gap: 1.5, mb: 1.5, flexWrap: "wrap",
  }}>
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, minWidth: 0 }}>
      {icon && (
        <Box sx={{
          width: 30, height: 30, borderRadius: `${pp.radius.sm}px`,
          bgcolor: pp.surface.mint, color: pp.color.green,
          display: "flex", alignItems: "center", justifyContent: "center",
          "& svg": { fontSize: 17 }, flexShrink: 0,
        }}>
          {icon}
        </Box>
      )}
      <Box sx={{ minWidth: 0 }}>
        {eyebrow && (
          <Typography sx={{
            fontSize: 10.5, fontWeight: 700, color: pp.color.muted,
            textTransform: "uppercase", letterSpacing: "0.08em", mb: 0.25,
          }}>
            {eyebrow}
          </Typography>
        )}
        <Typography sx={{ fontSize: 16, fontWeight: 700, color: pp.color.text, letterSpacing: "-0.01em" }}>
          {title}
        </Typography>
      </Box>
    </Box>
    {action}
  </Box>
);

type PortalEmptyStateProps = {
  icon: React.ReactNode;
  title: string;
  body?: string;
  action?: React.ReactNode;
  tone?: "green" | "blue" | "amber" | "navy";
  compact?: boolean;
};

export const PortalEmptyState: React.FC<PortalEmptyStateProps> = ({
  icon, title, body, action, tone = "green", compact = false,
}) => {
  const color = tone === "green" ? pp.color.green
    : tone === "blue"  ? pp.color.blue
    : tone === "amber" ? pp.color.amber
    : pp.color.navy;
  const surface = tone === "green" ? pp.surface.mint
    : tone === "blue"  ? pp.surface.blue
    : tone === "amber" ? pp.surface.amber
    : pp.surface.navy;

  return (
    <Box sx={{
      textAlign: "center", py: compact ? 3 : 5, px: 3,
    }}>
      <Box sx={{
        width: compact ? 46 : 60, height: compact ? 46 : 60,
        borderRadius: `${pp.radius.md}px`,
        bgcolor: surface, color, mx: "auto", mb: 1.5,
        display: "flex", alignItems: "center", justifyContent: "center",
        "& svg": { fontSize: compact ? 22 : 28 },
      }}>
        {icon}
      </Box>
      <Typography sx={{ fontSize: 14.5, fontWeight: 700, mb: 0.5, color: pp.color.text }}>
        {title}
      </Typography>
      {body && (
        <Typography sx={{ fontSize: 12.5, color: pp.color.muted, maxWidth: 380, mx: "auto", mb: action ? 2 : 0 }}>
          {body}
        </Typography>
      )}
      {action}
    </Box>
  );
};

type PortalStatusChipProps = {
  label: string;
  tone?: "green" | "amber" | "red" | "blue" | "navy" | "grey";
  size?: "sm" | "md";
};

export const PortalStatusChip: React.FC<PortalStatusChipProps> = ({
  label, tone = "grey", size = "sm",
}) => {
  const map: Record<string, { c: string; bg: string }> = {
    green: { c: pp.color.green, bg: pp.surface.mint },
    amber: { c: pp.color.amber, bg: pp.surface.amber },
    red:   { c: pp.color.red,   bg: pp.surface.red },
    blue:  { c: pp.color.blue,  bg: pp.surface.blue },
    navy:  { c: pp.color.navy,  bg: pp.surface.navy },
    grey:  { c: pp.color.muted, bg: pp.surface.grey },
  };
  const t = map[tone];
  return (
    <Box sx={{
      display: "inline-flex", alignItems: "center",
      px: size === "sm" ? 1 : 1.25, py: size === "sm" ? 0.25 : 0.5,
      bgcolor: t.bg, color: t.c,
      borderRadius: 999,
      fontSize: size === "sm" ? 10.5 : 12, fontWeight: 700,
      letterSpacing: "0.02em",
    }}>
      {label}
    </Box>
  );
};

type ChildIdentityCardProps = {
  studentName: string | undefined;
  gradeName?: string | null;
  status?: string | null;
  multiple?: boolean;
  onClick?: () => void;
  avatarSize?: number;
};

/**
 * Premium "Viewing Student" identity block. Used by ChildSelectorBar
 * as the always-visible child context. The picker menu itself is still
 * driven by the existing ChildContext in index.tsx.
 */
export const ChildIdentityCard: React.FC<ChildIdentityCardProps> = ({
  studentName, gradeName, status, multiple, onClick, avatarSize = 44,
}) => {
  const initials = (studentName || "?").split(/\s+/).filter(Boolean)
    .map((p) => p[0]).slice(0, 2).join("").toUpperCase() || "?";
  return (
    <Box
      onClick={onClick}
      sx={{
        display: "flex", alignItems: "center", gap: 1.5,
        p: 1.5, pr: 2,
        // Viewing Student strip — solid teal card fill (spec §"VIEWING
        // STUDENT CARD"). Previously a 3-stop linear-gradient wash;
        // now a clearly-coloured teal surface with a matching border.
        bgcolor: PP_C.teal.bg,
        border: `1px solid ${PP_C.teal.border}`,
        borderRadius: `${pp.radius.md}px`,
        boxShadow: pp.shadow.card,
        cursor: onClick ? "pointer" : "default",
        transition: pp.transition,
        "&:hover": onClick ? { borderColor: PP_C.teal.accent, boxShadow: pp.shadow.cardHover } : undefined,
      }}
    >
      <Avatar sx={{
        width: avatarSize, height: avatarSize, fontSize: 15, fontWeight: 700,
        bgcolor: PP_C.teal.accent,
        color: "#fff",
        boxShadow: `0 0 0 3px ${pp.color.card}, 0 0 0 4px rgba(15,118,110,0.20)`,
      }}>
        {initials}
      </Avatar>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{
          fontSize: 10, fontWeight: 700, color: pp.color.muted,
          textTransform: "uppercase", letterSpacing: "0.08em",
        }}>
          Viewing Student
        </Typography>
        <Typography sx={{
          fontSize: 15.5, fontWeight: 800, color: pp.color.text,
          overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
          letterSpacing: "-0.01em",
        }}>
          {studentName || "No student selected"}
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mt: 0.25 }}>
          {gradeName && (
            <Typography sx={{ fontSize: 11.5, color: pp.color.muted, fontWeight: 500 }}>
              {gradeName}
            </Typography>
          )}
          {status && (
            <PortalStatusChip label={status} tone="green" size="sm" />
          )}
        </Box>
      </Box>
      {multiple && (
        <Box sx={{ color: pp.color.muted, fontSize: 22, lineHeight: 1, pr: 0.5 }}>▾</Box>
      )}
    </Box>
  );
};
